import { useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

const ROLE_DIRTY_ACTION = {
  CLOSE: 'close',
  NAVIGATION: 'navigation',
} as const;

type RoleDirtyAction = (typeof ROLE_DIRTY_ACTION)[keyof typeof ROLE_DIRTY_ACTION];

export interface UseRoleUnsavedChangesOptions {
  onDiscard: () => void;
}

export interface UseRoleUnsavedChangesResult {
  isDirty: boolean;
  confirmOpen: boolean;
  requestDiscard: () => void;
  handleDiscard: () => void;
  handleKeepEditing: () => void;
  setIsDirty: (isDirty: boolean) => void;
  markClean: () => void;
}

export function useRoleUnsavedChanges({
  onDiscard,
}: UseRoleUnsavedChangesOptions): UseRoleUnsavedChangesResult {
  const [isDirty, setIsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dirtyAction, setDirtyAction] = useState<RoleDirtyAction | null>(null);
  const allowNavigationRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !allowNavigationRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
  );

  useBeforeUnload((event) => {
    if (!isDirty) return;
    event.preventDefault();
    event.returnValue = '';
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    setDirtyAction(ROLE_DIRTY_ACTION.NAVIGATION);
    setConfirmOpen(true);
  }, [blocker.state]);

  const requestDiscard = () => {
    if (!isDirty) {
      onDiscard();
      return;
    }

    setDirtyAction(ROLE_DIRTY_ACTION.CLOSE);
    setConfirmOpen(true);
  };

  const handleDiscard = () => {
    const action = dirtyAction;
    setConfirmOpen(false);
    setDirtyAction(null);
    setIsDirty(false);
    allowNavigationRef.current = true;

    if (action === ROLE_DIRTY_ACTION.NAVIGATION && blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }

    if (action === ROLE_DIRTY_ACTION.CLOSE) {
      onDiscard();
    }
  };

  const handleKeepEditing = () => {
    setConfirmOpen(false);
    setDirtyAction(null);
    if (blocker.state === 'blocked') blocker.reset();
  };

  const markClean = () => {
    setIsDirty(false);
    setConfirmOpen(false);
    setDirtyAction(null);
    allowNavigationRef.current = true;
  };

  return {
    isDirty,
    confirmOpen,
    requestDiscard,
    handleDiscard,
    handleKeepEditing,
    setIsDirty,
    markClean,
  };
}
